import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PageHero from '../../components/ui/page-hero';
import Footer from '../../components/ui/footer';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Scale, 
  FileText, 
  Shield, 
  DollarSign, 
  Users, 
  Mail, 
  Phone, 
  MapPin,
  CheckCircle,
  Gavel,
  Handshake,
  Building2,
  Globe,
  ArrowRight,
  Award,
  Zap,
  TrendingUp,
  Sparkles,
  Rocket,
  Crown,
  Gem,
  Brain,
  Play,
  MessageCircle,
  PenTool,
  Code,
  Key
} from 'lucide-react';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');
  const [readProgress, setReadProgress] = useState(0);
  const [isAgreed, setIsAgreed] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);

  // Enhanced navigation sections with design system colors
  const navigationSections = useMemo(() => [
    { id: 'services-covered', title: 'Services Covered', icon: Rocket, badge: '5 Services' },
    { id: 'client-obligations', title: 'Client Obligations', icon: Users, badge: '4 Points' },
    { id: 'payment-terms', title: 'Payment Terms', icon: DollarSign, badge: 'Flexible' },
    { id: 'intellectual-property', title: 'Intellectual Property', icon: Crown, badge: 'Protected' },
    { id: 'limitations', title: 'Limitations', icon: Shield, badge: 'Clear' },
    { id: 'dispute-resolution', title: 'Dispute Resolution', icon: Gavel, badge: 'Fair' },
    { id: 'agreement-process', title: 'Agreement Process', icon: Handshake, badge: 'Simple' },
    { id: 'contact-legal', title: 'Legal Contact', icon: Scale, badge: '24/7' }
  ], []);

  // Scroll spy functionality with progress tracking
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    navigationSections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    // Progress tracking
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setReadProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', updateProgress);
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', updateProgress);
    };
  }, [navigationSections]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Service data with design system colors
  const servicesData = [
    {
      icon: Rocket,
      title: 'Web Development & Design',
      description: 'Custom websites that convert visitors into customers',
      features: ['Responsive Design', 'SEO Optimized', 'Fast Loading']
    },
    {
      icon: Brain,
      title: 'AI Solutions & Automation',
      description: 'Intelligent systems that work 24/7 for your business',
      features: ['Machine Learning', 'Process Automation', 'Predictive Analytics']
    },
    {
      icon: Building2,
      title: 'CRM Systems & Optimization',
      description: 'Streamlined business processes for maximum efficiency',
      features: ['Customer Management', 'Sales Pipeline', 'Data Analytics']
    },
    {
      icon: TrendingUp,
      title: 'Digital Marketing & Social Media',
      description: 'Data-driven marketing strategies that deliver results',
      features: ['Social Media', 'Content Strategy', 'Performance Tracking']
    },
    {
      icon: Zap,
      title: 'SaaS Development & Co-Build',
      description: 'Scalable software solutions built for growth',
      features: ['Cloud Architecture', 'API Integration', 'Scalable Design']
    }
  ];

  const paymentMilestones = [
    { phase: 'Project Start', percentage: 30, icon: Play, description: 'Initial payment upon contract signing' },
    { phase: 'Milestone Completion', percentage: 40, icon: CheckCircle, description: 'Payment upon major milestone delivery' },
    { phase: 'Final Delivery', percentage: 30, icon: Award, description: 'Final payment upon project completion' }
  ];

  const agreementSteps = [
    { step: 1, title: 'Initial Consultation', icon: MessageCircle, description: 'We discuss your needs and project scope' },
    { step: 2, title: 'Proposal & Quote', icon: FileText, description: 'Detailed proposal with timeline and pricing' },
    { step: 3, title: 'Contract Signing', icon: PenTool, description: 'Both parties sign the service agreement' },
    { step: 4, title: 'Project Kickoff', icon: Rocket, description: 'Project begins with initial payment' },
    { step: 5, title: 'Development & Review', icon: Code, description: 'Regular updates and milestone reviews' },
    { step: 6, title: 'Final Delivery', icon: Award, description: 'Project completion and final payment' }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service | Envael - Creative Legal Framework</title>
        <meta name="description" content="Experience Envael's innovative Terms of Service. Interactive legal framework with creative design for our web development, AI, and digital transformation services." />
        <meta name="keywords" content="terms of service, creative legal terms, Envael agreement, interactive terms, Morocco legal, service contract" />
        <meta property="og:title" content="Terms of Service | Envael - Creative Legal Framework" />
        <meta property="og:description" content="Interactive and creative Terms of Service that make legal agreements engaging and transparent." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms of Service | Envael" />
        <meta name="twitter:description" content="Creative legal framework that protects both parties." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <PageHero
          pageName="Terms of Service"
          subtitle="Creative legal framework that protects both parties. Interactive, transparent, and designed for the modern digital age."
          size="large"
          fullHeight={true}
          enableSpline={true}
        />

        {/* Progress Bar */}
        <div className="fixed top-16 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
          <div className="max-w-7xl mx-auto px-4 py-2">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>Reading Progress</span>
              <span>{Math.round(readProgress)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 mt-1">
              <div 
                className="bg-primary h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${readProgress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className="lg:hidden bg-background border-b border-border sticky top-32 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex overflow-x-auto space-x-4">
              {navigationSections.map((section) => {
                const IconComponent = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all duration-300 flex items-center space-x-2 ${
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground shadow-lg scale-105'
                        : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-accent hover:scale-105'
                    }`}
                  >
                    <IconComponent className="h-3 w-3" />
                    <span className="whitespace-nowrap">{section.title}</span>
                    <Badge variant="secondary" className="ml-1 text-xs">{section.badge}</Badge>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content with Enhanced Sidebar */}
        <div className="flex max-w-7xl mx-auto">
          {/* Creative Sidebar Navigation */}
          <div className="hidden lg:block w-80 p-6 sticky top-32 h-fit">
            <div className="bg-muted/30 rounded-xl p-6 border border-border shadow-xl">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Scale className="h-5 w-5 text-primary-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Legal Framework</h3>
                  <p className="text-xs text-muted-foreground">Interactive Terms</p>
                </div>
              </div>
              
              <nav className="space-y-3">
                {navigationSections.map((section, index) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg text-sm transition-all duration-300 flex items-center space-x-3 group ${
                        activeSection === section.id
                          ? 'bg-primary text-primary-foreground shadow-lg transform scale-105'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent/50 hover:transform hover:scale-105'
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        activeSection === section.id 
                          ? 'bg-primary-foreground/20' 
                          : 'bg-accent'
                      }`}>
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium">{section.title}</div>
                        <div className="text-xs opacity-70">{section.badge}</div>
                      </div>
                      {activeSection === section.id && (
                        <Sparkles className="h-4 w-4 animate-pulse" />
                      )}
                    </button>
                  );
                })}
              </nav>
              
              {/* Agreement Status */}
              <div className="mt-6 pt-6 border-t border-border">
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`w-3 h-3 rounded-full ${isAgreed ? 'bg-green-500 animate-pulse' : 'bg-muted'}`} />
                  <span className="text-sm font-medium">Agreement Status</span>
                </div>
                <Button 
                  onClick={() => setIsAgreed(!isAgreed)}
                  variant={isAgreed ? "default" : "outline"}
                  className="w-full"
                >
                  {isAgreed ? (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Agreement Accepted
                    </>
                  ) : (
                    <>
                      <Handshake className="mr-2 h-4 w-4" />
                      Accept Terms
                    </>
                  )}
                </Button>
              </div>

              {/* Back to Top */}
              <div className="mt-4">
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="w-full text-center px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-all duration-300 hover:transform hover:scale-105"
                >
                  ↑ Back to Top
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">

            {/* Services Covered - Creative Grid */}
            <section id="services-covered" className="py-12 mb-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Rocket className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Services Covered</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Our comprehensive suite of professional services designed to transform your business
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {servicesData.map((service, index) => {
                  const IconComponent = service.icon;
                  return (
                    <Card 
                      key={index} 
                      className="group hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105 hover:-translate-y-2"
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <CardContent className="p-6">
                        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-4 group-hover:rotate-12 transition-transform duration-300">
                          <IconComponent className="h-8 w-8 text-primary-foreground" />
                        </div>
                        <h3 className="text-xl font-bold text-foreground mb-2">{service.title}</h3>
                        <p className="text-muted-foreground mb-4">{service.description}</p>
                        <div className="space-y-2">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                              <span className="text-sm text-muted-foreground">{feature}</span>
                            </div>
                          ))}
                        </div>
                        {hoveredCard === index && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <Button variant="outline" size="sm" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                              Learn More <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              </section>

            {/* Client Obligations - Interactive Cards */}
            <section id="client-obligations" className="py-12 bg-muted/30 mb-8 rounded-2xl">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Users className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Client Obligations</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Clear responsibilities that ensure successful project delivery and mutual satisfaction
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { icon: MessageCircle, title: 'Timely Communication', description: 'Provide feedback and responses within agreed timeframes' },
                  { icon: FileText, title: 'Accurate Requirements', description: 'Supply detailed and accurate project specifications' },
                  { icon: DollarSign, title: 'Payment Schedule', description: 'Make payments according to agreed milestones' },
                  { icon: Key, title: 'System Access', description: 'Provide necessary content, materials, and system access' }
                ].map((obligation, index) => {
                  const IconComponent = obligation.icon;
                  return (
                    <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 border-l-4 border-l-primary">
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <IconComponent className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">{obligation.title}</h3>
                            <p className="text-muted-foreground">{obligation.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              </section>

            {/* Payment Terms - Interactive Timeline */}
            <section id="payment-terms" className="py-12 mb-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <DollarSign className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Payment Terms</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Transparent, structured payment terms that ensure project continuity and fair compensation
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="relative">
                  {/* Timeline Line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-primary"></div>
                  
                  <div className="space-y-8">
                    {paymentMilestones.map((milestone, index) => {
                      const IconComponent = milestone.icon;
                      return (
                        <div key={index} className="relative flex items-center space-x-8">
                          <div className="relative z-10 w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
                            <IconComponent className="h-8 w-8 text-primary-foreground" />
                          </div>
                          <div className="flex-1">
                            <Card className="hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105">
                              <CardContent className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                  <h3 className="text-xl font-bold text-foreground">{milestone.phase}</h3>
                                  <Badge className="bg-primary text-primary-foreground">
                                    {milestone.percentage}%
                                  </Badge>
                                </div>
                                <p className="text-muted-foreground">{milestone.description}</p>
                                <div className="mt-4">
                                  <div className="w-full bg-muted rounded-full h-2">
                                    <div 
                                      className="bg-primary h-2 rounded-full transition-all duration-1000"
                                      style={{ width: `${milestone.percentage}%` }}
                                    />
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>
              </section>

            {/* Intellectual Property - Creative Showcase */}
            <section id="intellectual-property" className="py-12 bg-muted/30 mb-8 rounded-2xl">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Crown className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Intellectual Property</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Clear ownership and usage rights for all intellectual property created during our engagement
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="group hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105 border-l-4 border-l-primary">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                        <Users className="h-8 w-8 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-foreground">Client Ownership</h3>
                        <p className="text-muted-foreground">What you retain</p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {[
                        'Full ownership of provided content and branding',
                        'Complete ownership of project-specific deliverables',
                        'Rights to all custom code and designs',
                        'Exclusive use of all created materials'
                      ].map((item, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="group hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105 border-l-4 border-l-accent">
                  <CardContent className="p-8">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center">
                        <Gem className="h-8 w-8 text-accent-foreground" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-foreground">Envael Rights</h3>
                        <p className="text-muted-foreground">What we retain</p>
                      </div>
                    </div>
                    <ul className="space-y-3">
                      {[
                        'Ownership of proprietary methodologies and frameworks',
                        'Rights to general knowledge and expertise gained',
                        'Ability to showcase work in our portfolio',
                        'Third-party components subject to their licenses'
                      ].map((item, index) => (
                        <li key={index} className="flex items-start space-x-3">
                          <Shield className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                  </ul>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Limitations - Alert Cards */}
            <section id="limitations" className="py-12 mb-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Shield className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Limitations & Disclaimers</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Important limitations and disclaimers that apply to our services
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[
                  { icon: Globe, title: 'Service Availability', description: 'We strive for 99.9% uptime but cannot guarantee uninterrupted service', type: 'warning' },
                  { icon: Building2, title: 'Third-Party Dependencies', description: 'Our services may depend on third-party platforms and services', type: 'info' },
                  { icon: FileText, title: 'Content Accuracy', description: 'We are not responsible for the accuracy of client-provided content', type: 'warning' },
                  { icon: TrendingUp, title: 'Scope Changes', description: 'Significant scope changes may affect timeline and cost', type: 'info' }
                ].map((limitation, index) => {
                  const IconComponent = limitation.icon;
                  const isWarning = limitation.type === 'warning';
                  return (
                    <Card key={index} className={`group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 border-l-4 ${isWarning ? 'border-l-destructive' : 'border-l-primary'}`}>
                      <CardContent className="p-6">
                        <div className="flex items-start space-x-4">
                          <div className={`w-12 h-12 ${isWarning ? 'bg-destructive/10' : 'bg-primary/10'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <IconComponent className={`h-6 w-6 ${isWarning ? 'text-destructive' : 'text-primary'}`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-foreground mb-2">{limitation.title}</h3>
                            <p className="text-muted-foreground">{limitation.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                </div>
              </section>

            {/* Dispute Resolution - Process Flow */}
            <section id="dispute-resolution" className="py-12 bg-muted/30 mb-8 rounded-2xl">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Gavel className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Dispute Resolution</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Fair and structured process for resolving any disputes that may arise
                </p>
              </div>

              <div className="max-w-4xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {[
                    { step: 1, icon: MessageCircle, title: 'Direct Communication', description: 'Open dialogue and negotiation' },
                    { step: 2, icon: Handshake, title: 'Mediation', description: 'Neutral third party assistance' },
                    { step: 3, icon: Scale, title: 'Arbitration', description: 'Binding resolution process' },
                    { step: 4, icon: Gavel, title: 'Legal Action', description: 'Court proceedings if needed' }
                  ].map((step, index) => {
                    const IconComponent = step.icon;
                    return (
                      <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center">
                        <CardContent className="p-6">
                          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                            <IconComponent className="h-8 w-8 text-primary-foreground" />
                          </div>
                          <div className="text-2xl font-bold text-primary mb-2">Step {step.step}</div>
                          <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                          <p className="text-sm text-muted-foreground">{step.description}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                </div>
              </section>

            {/* Agreement Process - Interactive Timeline */}
            <section id="agreement-process" className="py-12 mb-8">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Handshake className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Agreement Process</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Simple, transparent process from initial contact to project completion
                </p>
              </div>

              <div className="max-w-5xl mx-auto">
                <div className="relative">
                  {/* Connection Lines */}
                  <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-primary"></div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {agreementSteps.map((step, index) => {
                      const IconComponent = step.icon;
                      return (
                        <div key={index} className="relative">
                          <Card className="group hover:shadow-2xl transition-all duration-500 hover:transform hover:scale-105 hover:-translate-y-2 text-center">
                            <CardContent className="p-6">
                              <div className="w-20 h-20 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300 shadow-lg">
                                <IconComponent className="h-10 w-10 text-primary-foreground" />
                              </div>
                              <div className="text-3xl font-bold text-primary mb-2">{step.step}</div>
                              <h3 className="text-xl font-bold text-foreground mb-3">{step.title}</h3>
                              <p className="text-muted-foreground">{step.description}</p>
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                </div>
                </div>
              </section>

            {/* Contact Information - Creative Contact Cards */}
            <section id="contact-legal" className="py-12 bg-muted/30 mb-8 rounded-2xl">
              <div className="text-center mb-12">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                    <Scale className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <h2 className="text-3xl font-bold text-foreground">Legal Contact Information</h2>
                </div>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Questions about these terms? We're here to help with any clarifications you need.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <Card className="group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                      <Mail className="h-8 w-8 text-primary-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Email Support</h3>
                    <p className="text-muted-foreground mb-4">Get detailed responses to your legal questions</p>
                    <p className="text-sm font-mono bg-muted px-3 py-2 rounded-lg">legal@envael.com</p>
                  </CardContent>
                </Card>

                <Card className="group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-accent rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                      <Phone className="h-8 w-8 text-accent-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Phone Support</h3>
                    <p className="text-muted-foreground mb-4">Speak directly with our legal team</p>
                    <p className="text-sm font-mono bg-muted px-3 py-2 rounded-lg">+212 6 63 08 13 83</p>
                  </CardContent>
                </Card>

                <Card className="group hover:shadow-xl transition-all duration-300 hover:transform hover:scale-105 text-center">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:rotate-12 transition-transform duration-300">
                      <MapPin className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Office Location</h3>
                    <p className="text-muted-foreground mb-4">Visit our headquarters in Morocco</p>
                    <p className="text-sm font-mono bg-muted px-3 py-2 rounded-lg">287 BLOC K APPT 2 ETAGE 1 OULED OUJIH KENITRA</p>
                  </CardContent>
                </Card>
              </div>

              {/* Call to Action */}
              <div className="text-center mt-12">
                <div className="bg-gradient-to-r from-primary/10 to-primary/5 p-8 rounded-2xl max-w-2xl mx-auto">
                  <h3 className="text-2xl font-bold text-foreground mb-4">
                    Ready to Start Your Project?
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Let's discuss how we can help transform your business with our innovative solutions.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button size="lg" className="group">
                      <Handshake className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                      Start Your Project
                    </Button>
                    <Button variant="outline" size="lg" className="group">
                      <FileText className="mr-2 h-5 w-5 group-hover:rotate-12 transition-transform" />
                      Download Terms
                    </Button>
                  </div>
                  </div>
                </div>
              </section>

          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default TermsOfService;